import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

import { Box, CircularProgress, Typography } from "@mui/material";
import { collection, getDocs } from "firebase/firestore";

import { useAuth } from "../../contexts/AuthContext";
import { db } from "../../firebase";

const SourceRevenueTopChart = ({ selectedYear }) => {
  const { currentUser } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser?.uid) return;
      setLoading(true);

      try {
        const customersSnapshot = await getDocs(collection(db, `users/${currentUser.uid}/customers`));
        const customerSourceById = {};

        customersSnapshot.forEach(currentDoc => {
          const customer = currentDoc.data();
          customerSourceById[currentDoc.id] = customer.source || "Non renseignee";
        });

        const ordersSnapshot = await getDocs(collection(db, `users/${currentUser.uid}/orders`));
        const revenueBySource = {};

        ordersSnapshot.forEach(currentDoc => {
          const order = currentDoc.data();
          if (!order?.selectedDate || !order?.orderIsConfirmed) return;
          if (order.orderIsCanceled) return;

          const date = new Date(order.selectedDate);
          if (Number.isNaN(date.getTime())) return;
          if (date.getFullYear() !== selectedYear) return;

          const source = customerSourceById[order.client_id] || "Non renseignee";
          revenueBySource[source] = (revenueBySource[source] || 0) + Number(order.totalPrice || 0);
        });

        const sortedTop5 = Object.entries(revenueBySource)
          .map(([source, revenue]) => ({
            source,
            revenue: Number(revenue.toFixed(2))
          }))
          .sort((a, b) => b.revenue - a.revenue)
          .slice(0, 5)
          .reverse();

        setData(sortedTop5);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, selectedYear]);

  if (loading) {
    return (
      <Box sx={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data.length) {
    return (
      <Box sx={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Aucune donnee pour cette annee
        </Typography>
      </Box>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={data} layout="vertical" margin={{ top: 6, right: 18, left: 8, bottom: 6 }}>
        <CartesianGrid stroke="rgba(100, 116, 139, 0.15)" strokeDasharray="4 4" horizontal={false} />
        <XAxis
          type="number"
          tick={{ fill: "#64748b", fontSize: 12 }}
          tickLine={false}
          axisLine={false}
          tickFormatter={value => `${Math.round(value)}€`}
        />
        <YAxis
          type="category"
          dataKey="source"
          tick={{ fill: "#475569", fontSize: 12 }}
          tickLine={false}
          axisLine={false}
          width={130}
        />
        <Tooltip formatter={value => `${Number(value || 0).toLocaleString("fr-FR")} €`} />
        <Bar dataKey="revenue" fill="#d4a411" radius={[0, 8, 8, 0]} barSize={22} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default SourceRevenueTopChart;
